/**
 * Welcome-flow GraphQL hooks. Three mutations + one query for the
 * /welcome page and the post-auth router.
 *
 * Wire shape mirrors `graphql_link_session.ts` — react-query
 * `useMutation` wrapping an Apollo client mutation/query call. We
 * keep it consistent with the rest of the auth-side hooks rather
 * than introducing a new pattern just for this page.
 */
import {ApolloClient, gql, NormalizedCacheObject} from '@apollo/client';
import {useMutation} from 'react-query';

export type WelcomeStep =
  | 'PRINCIPLES'
  | 'GOOGLE_ACCOUNT'
  | 'DESKTOP_INSTALLED'
  | 'KID_SIGNED_IN';

export interface WelcomeState {
  principlesAcknowledged: boolean;
  googleAccountSetUp: boolean;
  desktopInstalled: boolean;
  kidSignedIn: boolean;
  completedAt: number | null;
  skippedAt: number | null;
}

const WELCOME_STATE_FIELDS = `
  principlesAcknowledged
  googleAccountSetUp
  desktopInstalled
  kidSignedIn
  completedAt
  skippedAt
`;

// Single fetch that the post-auth router and the /welcome page
// both use — pulls the user record + welcomeState + the linked
// profile count in one round-trip so the router can decide where
// to send the parent without three separate calls.
export const GET_WELCOME_BUNDLE_QUERY = gql`
  query GetWelcomeBundle {
    getCurrentUser {
      uid
      welcomeState {
        ${WELCOME_STATE_FIELDS}
      }
    }
    getMyYouTubeProfiles {
      id
    }
  }
`;

export interface WelcomeBundle {
  welcomeState: WelcomeState | null;
  profileCount: number;
}

/**
 * One-shot fetch of welcomeState + profile count. Used by the
 * post-auth redirect logic (which decides whether to send the
 * parent to /welcome or /profile) and by the /welcome page itself
 * (to hydrate the checklist on mount). Bypasses Apollo cache so
 * `markWelcomeStep` mutations done elsewhere take effect on the
 * next call.
 */
export function useGetWelcomeBundle(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(async (): Promise<WelcomeBundle> => {
    const {data} = await client.query<{
      getCurrentUser: {welcomeState: WelcomeState | null} | null;
      getMyYouTubeProfiles: Array<{id: string}> | null;
    }>({
      query: GET_WELCOME_BUNDLE_QUERY,
      fetchPolicy: 'network-only',
    });
    return {
      welcomeState: data?.getCurrentUser?.welcomeState ?? null,
      profileCount: data?.getMyYouTubeProfiles?.length ?? 0,
    };
  });
}

const MARK_WELCOME_STEP = gql`
  mutation MarkWelcomeStep($step: WelcomeStep!) {
    markWelcomeStep(step: $step) {
      ${WELCOME_STATE_FIELDS}
    }
  }
`;

export function useMarkWelcomeStep(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(async (step: WelcomeStep): Promise<WelcomeState> => {
    const {data} = await client.mutate<{markWelcomeStep: WelcomeState}>({
      mutation: MARK_WELCOME_STEP,
      variables: {step},
    });
    if (!data?.markWelcomeStep) throw new Error('markWelcomeStep returned no data');
    return data.markWelcomeStep;
  });
}

const SKIP_WELCOME = gql`
  mutation SkipWelcome {
    skipWelcome {
      ${WELCOME_STATE_FIELDS}
    }
  }
`;

export function useSkipWelcome(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(async (): Promise<WelcomeState> => {
    const {data} = await client.mutate<{skipWelcome: WelcomeState}>({mutation: SKIP_WELCOME});
    if (!data?.skipWelcome) throw new Error('skipWelcome returned no data');
    return data.skipWelcome;
  });
}

const RESET_WELCOME_SKIP = gql`
  mutation ResetWelcomeSkip {
    resetWelcomeSkip {
      ${WELCOME_STATE_FIELDS}
    }
  }
`;

export function useResetWelcomeSkip(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(async (): Promise<WelcomeState> => {
    const {data} = await client.mutate<{resetWelcomeSkip: WelcomeState}>({
      mutation: RESET_WELCOME_SKIP,
    });
    if (!data?.resetWelcomeSkip) throw new Error('resetWelcomeSkip returned no data');
    return data.resetWelcomeSkip;
  });
}
